import React, {useState} from 'react';
import {Spin, TreeSelect} from 'antd';
import type {Process} from '../../types';
import {useProcess} from './hooks/useProcess';
import {useTranslation} from '../../hooks/useTranslation';
import {API_PARAMS} from "../../constants/constants";

interface SelectProcessProps {
  id?: string;
  value?: string | string[];
  placeholder?: string;
  onChange?: (value: string | string[], label?: any, extra?: any) => void;
  disabled?: boolean;
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  multiple?: boolean;
  treeCheckable?: boolean;
  showCheckedStrategy?: 'SHOW_ALL' | 'SHOW_PARENT' | 'SHOW_CHILD';
  treeDefaultExpandAll?: boolean;
  maxTagCount?: number | 'responsive';
  initialTreeData?: Process[];
}

const SelectProcess: React.FC<SelectProcessProps> = ({
                                                       id,
                                                       value,
                                                       placeholder,
                                                       onChange,
                                                       disabled = false,
                                                       allowClear = true,
                                                       size = 'middle',
                                                       style,
                                                       className,
                                                       multiple = true,
                                                       treeCheckable = true,
                                                       showCheckedStrategy = 'SHOW_PARENT',
                                                       initialTreeData
                                                     }) => {
  const {t} = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {data: processes = [], isLoading, error} = useProcess(
    {
      node: API_PARAMS.ZERO,
      depth: API_PARAMS.ZERO
    },
    {
      enabled: isDropdownOpen // Only fetch data when the dropdown is opened
    }
  );

  const handleDropdownVisibleChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const handleChange = (selectedValue: string | string[], label?: any, extra?: any) => {
    if (onChange) {
      onChange(selectedValue, label, extra);
    }
  };

  // keep a noop search handler to avoid uncontrolled search behavior if needed
  const handleSearch = (_searchText: string) => {
    // intentionally empty; we don't store search text in this component
  };

  const filterTreeNode = (inputValue: string, treeNode: any) => {
    return treeNode.title?.toLowerCase().includes(inputValue.toLowerCase()) || false;
  };

  if (error) {
    console.error('Error loading processes:', error);
  }

  // Use initialTreeData when provided (prefetched by parent), otherwise fall back to
  const treeDataToUse = initialTreeData && initialTreeData.length ? initialTreeData : processes;

  return (
    <TreeSelect
      id={id}
      value={value}
      placeholder={placeholder || t('messages.select')}
      onChange={handleChange}
      onSearch={handleSearch}
      onOpenChange={handleDropdownVisibleChange}
      disabled={disabled}
      allowClear={allowClear}
      size={size}
      style={style}
      className={className}
      multiple={multiple}
      listHeight={400}
      treeCheckable={treeCheckable}
      showCheckedStrategy={showCheckedStrategy}
      showSearch
      filterTreeNode={filterTreeNode}
      loading={isLoading}
      notFoundContent={isLoading ? <Spin size="small"/> : t('messages.notFound')}
      treeData={treeDataToUse}
      styles={{popup: {root: {maxHeight: 600}}}}
    />
  );
};

export default SelectProcess;
